import { displayControlStatusLabel } from '../../domain/overview/controlStatusDisplay';
import {
  displaySafetyManagerLabel,
  mapSafetyManagerFactLabels,
} from '../../domain/safetyManagerUxLabels';
import {
  incidentCanonicalFollowupOwnerLabel,
  incidentCanonicalRuleOwnerLabel,
} from '../../components/incidents/reviewCanonical';
import {
  readFollowupOwnerLabel,
  readPatternOwnerLabel,
} from '../../domain/incidents/incidentOwnershipSemantics';
import { ownerRoleIsUnassigned } from '../../domain/incidents/incidentPatternVocabulary';

export type SafetyReportReadinessState = 'draft' | 'requires_review' | 'ready' | 'blocked';

export const SAFETY_REPORT_READINESS_LABELS: Record<SafetyReportReadinessState, string> = {
  draft: 'Report draft',
  requires_review: 'Report requires review',
  ready: 'Report ready',
  blocked: 'Report blocked',
};

export const SAFETY_REPORT_CONTROL_SUBTITLE =
  'Evidence-ready report showing recurring risk patterns, pattern observations, ownership, corrective action status, and impact measurement readiness.';

export const SAFETY_REPORT_MVP_TECHNICAL_NOTE =
  'Technical scope settings and internal identifiers stay behind Advanced Technical Details.';

export const SAFETY_REPORT_DEFAULT_CONTROL_STATUS = 'Needs review';

export const SAFETY_REPORT_DEFAULT_EVIDENCE_PROOF = 'Limited — review required';

export const SAFETY_REPORT_MVP_SUMMARY =
  'A recurring risk pattern was observed in the selected operating area. Representative evidence is available, but human review is still required before the report can be finalized.';

export const SAFETY_REPORT_MVP_WHAT_HAPPENED =
  'Camera observations were grouped into a recurring risk pattern during the selected time window.';

export const SAFETY_REPORT_MVP_RISK_PATTERN =
  'This recurring risk pattern shows repeated exposure in the selected area. Multiple camera observations have been grouped into one reviewable recurring risk pattern for this report.';

export const SAFETY_REPORT_MVP_EVIDENCE_PACKAGE = [
  '1 representative evidence sample selected',
  '2,175 camera observations grouped into one recurring risk pattern',
  'Event timeline available',
  'Detection and review details available',
  'Video clip pending',
  'Snapshot pending',
] as const;

export const SAFETY_REPORT_MVP_REVIEW_STATUS =
  'Representative evidence is available, but human review is not complete.';

export const SAFETY_REPORT_MVP_REVIEW_CHECKLIST = [
  'Confirm whether this recurring risk pattern requires corrective action',
  `Assign ${displaySafetyManagerLabel('pattern_owner').toLowerCase()}`,
  `Assign ${displaySafetyManagerLabel('followup_owner').toLowerCase()}`,
  'Record corrective action',
  'Complete impact measurement',
  'Mark the report ready only after review is complete',
] as const;

export const SAFETY_REPORT_MVP_REPORT_SCOPE = {
  reviewOutcome:
    '1 representative evidence sample requires review. No confirmed dismissal, resolution, or escalation outcome has been recorded yet.',
  ownershipPattern: `${displaySafetyManagerLabel('pattern_owner')}: Not assigned`,
  ownershipFollowUp: `${displaySafetyManagerLabel('followup_owner')}: Not assigned`,
  escalation:
    'SLA clock active. Escalation will be triggered if review is not completed within the configured window.',
  correctiveAction: 'No corrective action or work order has been linked yet.',
  measurement:
    'Impact measurement has not been recorded. Verified improvement cannot be claimed yet.',
  actionWorkflow:
    `Next step: assign ${displaySafetyManagerLabel('pattern_owner').toLowerCase()}, assign ${displaySafetyManagerLabel('followup_owner').toLowerCase()}, record corrective action, or dismiss the evidence record with justification.`,
} as const;

export const SAFETY_REPORT_MVP_ACTION_SNAPSHOT =
  '1 representative evidence sample has no captured follow-up owner, work order, or next review time yet.';

export const SAFETY_REPORT_MVP_FOLLOWUP = {
  patternOwner: 'Not assigned',
  followUpOwner: 'Not assigned',
  currentAction: 'Create follow-up review',
  workflowDate: 'Follow-up date not recorded',
} as const;

export const SAFETY_REPORT_MVP_UNPROVEN =
  'This report shows that a recurring risk pattern was observed and that review context was captured. It does not yet prove that risk was reduced, because impact measurement has not been recorded. Media evidence is not included in this export.';

/** @deprecated Use download CTA labels; sign-off is informational only. */
export const SAFETY_REPORT_MVP_DISABLED_CTA = 'REPORT NOT READY FOR SIGN-OFF';

export const SAFETY_REPORT_MVP_DISABLED_CTA_HELPER =
  'Complete evidence review, action ownership, corrective action, and impact measurement before final sign-off.';

export const SAFETY_REPORT_SIGNOFF_PENDING_HELPER =
  'You can download this report now. Final sign-off still requires evidence review, action ownership, corrective action, and impact measurement.';

export const SAFETY_REPORT_DOWNLOAD_FAILED_HELPER =
  'The Safety Report ZIP could not be downloaded. Try again, or rebuild the package if the error continues.';

export const SAFETY_REPORT_BUILD_FAILED_HELPER =
  'The report package could not be built for this scope. Try again in a moment.';

export const SAFETY_REPORT_BUILD_TIMEOUT_HELPER =
  'The Scan Package is still building for this time window. Keep this page open and click Retry Scan Package Build — multi-day windows can take several minutes.';

export const SAFETY_REPORT_BUILD_INCOMPLETE_HELPER =
  'The report package came back incomplete (one or more report sections did not finish). Wait for any in-progress rebuild to finish, then click Retry Scan Package Build.';

export const SAFETY_REPORT_BUILD_SCOPE_HELPER =
  'Set the report time window in Report Scope, then click Build Report ZIP to generate the ZIP package.';

export const SAFETY_REPORT_MVP_DEFAULT_LATEST_EVENT = '5/22/2026, 5:21 PM';

export type SafetyReportTopStatusFact = { label: string; value: string };

export function patternOwnerIsAssigned(label?: string | null): boolean {
  return !ownerRoleIsUnassigned(label);
}

export function followUpOwnerIsAssigned(label?: string | null): boolean {
  return !ownerRoleIsUnassigned(label);
}

export function buildSafetyReportHeadline(args: {
  patternOwnerAssigned: boolean;
  followUpOwnerAssigned: boolean;
}): string {
  if (args.patternOwnerAssigned && !args.followUpOwnerAssigned) {
    return 'A recurring risk pattern was observed in the selected operating area. Representative evidence is available, but human review is still required before the report can be finalized. Risk ownership has started, but action ownership, corrective action, and impact measurement are still pending.';
  }
  return 'A recurring risk pattern was observed in the selected operating area. Representative evidence is available, but human review is still required before the report can be finalized. Risk ownership, action ownership, corrective action, and impact measurement are still pending.';
}

export function buildSafetyReportControlProgressLabel(args: {
  patternOwnerAssigned: boolean;
  followUpOwnerAssigned: boolean;
}): string {
  if (!args.patternOwnerAssigned) return '';
  if (args.followUpOwnerAssigned) return '';
  return `${displaySafetyManagerLabel('pattern_owner')} assigned; action ownership and impact measurement still pending.`;
}

export function buildSafetyReportReviewChecklist(patternOwnerAssigned: boolean): string[] {
  const patternOwnerStep = patternOwnerAssigned
    ? `Confirm ${displaySafetyManagerLabel('pattern_owner').toLowerCase()}`
    : `Assign ${displaySafetyManagerLabel('pattern_owner').toLowerCase()}`;
  return [
    'Confirm whether this recurring risk pattern requires corrective action',
    patternOwnerStep,
    'Assign action owner',
    'Record corrective action',
    'Complete impact measurement',
    'Mark report ready only after review is complete',
  ];
}

export function formatCameraCalibrationScopeLabel(
  artifact?: Record<string, unknown> | null,
  evidenceQuality?: Record<string, unknown> | null,
): string {
  const status = String(
    evidenceQuality?.camera_calibration_status
    || artifact?.camera_calibration_status
    || artifact?.status
    || '',
  ).trim().toLowerCase();
  if (status === 'floor_plane_calibrated') return 'Floor-plane calibrated';
  if (status === 'frame_normalized_only') return 'Camera view only';
  if (status === 'calibration_stale') return 'Calibration stale';
  if (status === 'not_calibrated') return 'Not calibrated';
  return status ? status.replace(/_/g, ' ') : 'Not assessed';
}

/** Secondary facts shown in Report scope / Evidence sections (not the top status card). */
export function buildSafetyReportSecondaryStatusFacts(args: {
  reviewStatusLabel?: string;
  evidenceProofLabel?: string;
  latestEventLabel?: string;
  followUpOwnerLabel?: string;
  controlProgressLabel?: string;
  cameraCalibrationLabel?: string;
  detectionBasisLabel?: string;
}): SafetyReportTopStatusFact[] {
  const latest = String(args.latestEventLabel || '').trim();
  const facts: SafetyReportTopStatusFact[] = [];
  const reviewStatus = String(args.reviewStatusLabel || '').trim();
  if (reviewStatus) {
    facts.push({ label: 'Review status', value: reviewStatus });
  }
  const progress = String(args.controlProgressLabel || '').trim();
  if (progress) {
    facts.push({ label: 'Progress', value: progress });
  }
  const calibration = String(args.cameraCalibrationLabel || '').trim();
  if (calibration) {
    facts.push({ label: 'Camera calibration', value: calibration });
  }
  const detectionBasis = String(args.detectionBasisLabel || '').trim();
  if (detectionBasis) {
    facts.push({ label: displaySafetyManagerLabel('measurement_basis'), value: detectionBasis });
  }
  facts.push(
    { label: displaySafetyManagerLabel('evidence_quality'), value: args.evidenceProofLabel || SAFETY_REPORT_DEFAULT_EVIDENCE_PROOF },
    {
      label: 'Latest event',
      value: latest && latest !== 'Not captured yet' && latest !== 'Not available yet'
        ? latest
        : SAFETY_REPORT_MVP_DEFAULT_LATEST_EVENT,
    },
    { label: displaySafetyManagerLabel('followup_owner'), value: args.followUpOwnerLabel || 'Not assigned' },
  );
  return mapSafetyManagerFactLabels(facts);
}

/** @deprecated Use buildSafetyReportTopStatusPanel for the summary card and buildSafetyReportSecondaryStatusFacts below. */
export function buildSafetyReportTopStatusFacts(args: {
  controlStatusLabel?: string;
  reviewStatusLabel?: string;
  evidenceProofLabel?: string;
  latestEventLabel?: string;
  followUpOwnerLabel?: string;
  controlProgressLabel?: string;
  cameraCalibrationLabel?: string;
}): SafetyReportTopStatusFact[] {
  return buildSafetyReportSecondaryStatusFacts(args);
}

export function formatReportScopeCollapsedSummary(args: {
  ruleLabel: string;
  cameraLabel: string;
  areaLabel: string;
  startLabel: string;
  endLabel: string;
  representativeCaseCount: number;
}): string {
  const rule = String(args.ruleLabel || '').trim() || 'Safety rule not set';
  const camera = String(args.cameraLabel || '').trim() || 'All cameras';
  const area = normalizeSafetyReportAreaLabel(args.areaLabel);
  const start = String(args.startLabel || '').trim() || 'start not set';
  const end = String(args.endLabel || '').trim() || 'end not set';
  const count = Math.max(0, Math.floor(Number(args.representativeCaseCount) || 0));
  const caseLabel = count === 1 ? '1 representative evidence sample' : `${count} representative evidence samples`;
  return `Report scope: ${rule} · ${camera} · ${area} · ${start}–${end} · ${caseLabel}`;
}

export function formatGroupedEventSignalsCopy(count: number): string {
  const normalized = Math.max(0, Math.floor(Number(count) || 0));
  if (normalized <= 0) {
    return 'Camera observations grouped into this recurring risk pattern';
  }
  const formatted = normalized.toLocaleString('en-US');
  const noun = normalized === 1 ? 'camera observation' : 'camera observations';
  return `${formatted} ${noun} grouped into this recurring risk pattern`;
}

export function formatSafetyReportOwnerLabel(raw: string | null | undefined): string {
  const label = String(raw || '').trim();
  return label || 'Not assigned';
}

export function buildSafetyReportOwnershipScopeSummary(args: {
  patternOwnerLabel: string;
  followUpOwnerLabel: string;
}): string {
  const patternOwner = formatSafetyReportOwnerLabel(args.patternOwnerLabel);
  const followUpOwner = formatSafetyReportOwnerLabel(args.followUpOwnerLabel);
  return `${displaySafetyManagerLabel('pattern_owner')}: ${patternOwner}. ${displaySafetyManagerLabel('followup_owner')}: ${followUpOwner}.`;
}

export function buildRepresentativeCaseAreaPatternSummary(areaLabel: string): string {
  const area = normalizeSafetyReportAreaLabel(areaLabel);
  return `${area} accounts for the selected representative evidence in this report window.`;
}

export function isIncidentCountRiskPatternSummary(summary: string): boolean {
  const line = String(summary || '').trim();
  return /accounts for/i.test(line) && /incident/i.test(line) && /report window|proof window/i.test(line);
}

export function deriveSafetyReportPatternOwnerLabel(
  reportScopeIncidents: Array<Record<string, unknown>>,
): string {
  for (const incident of reportScopeIncidents) {
    const owner = readPatternOwnerLabel(incident as any);
    if (owner) return owner;
  }
  return 'Not assigned';
}

export function deriveSafetyReportFollowUpOwnerLabel(
  reportScopeIncidents: Array<Record<string, unknown>>,
  projectionOwner?: string | null,
): string {
  const projected = String(projectionOwner || '').trim();
  if (projected) return projected;
  for (const incident of reportScopeIncidents) {
    const canonical = incidentCanonicalFollowupOwnerLabel(incident as any);
    if (canonical) return canonical;
  }
  for (const incident of reportScopeIncidents) {
    const owner = readFollowupOwnerLabel(incident as any);
    if (owner) return owner;
  }
  return 'Not assigned';
}

export function deriveSafetyReportRuleOwnerLabel(
  reportScopeIncidents: Array<Record<string, unknown>>,
): string {
  for (const incident of reportScopeIncidents) {
    const owner = incidentCanonicalRuleOwnerLabel(incident as any);
    if (owner) return owner;
  }
  return 'Not assigned';
}

export function resolveSafetyReportControlStatusLabel(args: {
  controlStatusKey?: string | null;
  apiLabel?: string | null;
  reviewComplete?: boolean;
  outcomeRecorded?: boolean;
}): string {
  if (args.outcomeRecorded) return displayControlStatusLabel('controlled', args.apiLabel);
  if (!args.reviewComplete) return SAFETY_REPORT_DEFAULT_CONTROL_STATUS;
  const key = String(args.controlStatusKey || '').trim().toLowerCase();
  const apiLabel = String(args.apiLabel || '').trim().toLowerCase();
  if (
    key === 'needs_review'
    || apiLabel === 'needs review'
    || apiLabel === 'pending review'
  ) {
    return SAFETY_REPORT_DEFAULT_CONTROL_STATUS;
  }
  if (
    key === 'owner_assigned'
    || key === 'action_pending'
    || key === 'under_followup'
    || key === 'measurement_pending'
    || apiLabel === 'owner assigned'
  ) {
    return 'Review required';
  }
  if (key === 'exposed' || apiLabel === 'control not verified' || apiLabel === 'exposed') {
    return 'Review required';
  }
  if (key) return displayControlStatusLabel(key, args.apiLabel);
  return 'Review required';
}

export function resolveSafetyReportEvidenceProofLabel(args: {
  readinessLabel?: string | null;
  clipPending?: boolean;
  generating?: boolean;
}): string {
  const readiness = String(args.readinessLabel || '').trim();
  if (readiness === 'Ready with limitations') return SAFETY_REPORT_DEFAULT_EVIDENCE_PROOF;
  if (readiness === 'Ready') return SAFETY_REPORT_DEFAULT_EVIDENCE_PROOF;
  if (args.generating) return 'Pending';
  if (args.clipPending) return 'Pending';
  if (readiness) return readiness;
  return SAFETY_REPORT_DEFAULT_EVIDENCE_PROOF;
}

export function normalizeSafetyReportAreaLabel(raw: string): string {
  const label = String(raw || '').trim();
  if (!label) return 'Area not assigned';
  if (/^unavailable$/i.test(label)) return 'Area not assigned';
  if (label === 'the selected area' || label === 'the selected operating area') return 'Area not assigned';
  return label;
}

export function resolveSafetyReportReadinessState(args: {
  hasSelectedSafetyEvent: boolean;
  hasCapturedEvidence: boolean;
  reviewComplete: boolean;
  ownerAssigned: boolean;
  correctiveLinked: boolean;
  followUpMeasured: boolean;
}): SafetyReportReadinessState {
  if (!args.hasSelectedSafetyEvent) return 'blocked';
  if (
    args.reviewComplete
    && args.ownerAssigned
    && args.correctiveLinked
    && args.followUpMeasured
  ) {
    return 'ready';
  }
  if (args.hasSelectedSafetyEvent && args.hasCapturedEvidence) return 'requires_review';
  return 'draft';
}

export function shouldUseSafetyReportMvpRollupCopy(args: {
  loading?: boolean;
  totalIncidents: number;
  reviewComplete: boolean;
}): boolean {
  if (args.reviewComplete) return false;
  if (args.loading) return true;
  if (args.totalIncidents <= 0) return true;
  if (args.totalIncidents > 10) return true;
  return false;
}

export function shouldPreferSafetyReportMvpPresentation(args: {
  hasSelectedSafetyEvent: boolean;
  reviewComplete: boolean;
}): boolean {
  return args.hasSelectedSafetyEvent && !args.reviewComplete;
}

export function shouldPreferSafetyReportMvpEvidenceFact(args: {
  hasSelectedSafetyEvent: boolean;
  reviewComplete: boolean;
}): boolean {
  return shouldPreferSafetyReportMvpPresentation(args);
}

export function formatSafetyReportSelectionSummary(args: {
  areaLabel: string;
  fromLabel: string;
  toLabel: string;
}): string {
  const area = normalizeSafetyReportAreaLabel(args.areaLabel);
  const from = String(args.fromLabel || '').trim() || 'start not set';
  const to = String(args.toLabel || '').trim() || 'end not set';
  return `Showing recurring risk pattern in ${area} from ${from} to ${to}.`;
}

/** @deprecated use formatGroupedEventSignalsCopy */
export function formatGroupedRawSignalsCopy(count: number): string {
  return formatGroupedEventSignalsCopy(count);
}
